import { ApiEndpoints } from "@shared/api/api-endpoints";
import {
  bxApiClient,
  cxApiClient,
  onboardingApiClient,
} from "@shared/api/clients/apiClientFactory";
import { QueryStringParameters } from "@shared/api/url-builder";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { BillCycleDetail, BillCycleResponse, BillRunData, BillRunResponse, Choices, PaginatedBillCycleResponse, PaginatedOutstandingBillsResponse, PaginatedResponsePremise, SubArea } from "./types";
import { PAGE_SIZE } from "@shared/utils/constants";
import { toSnakeCase } from "@shared/utils/caseConverter";

export const getChoices = async (choice: string): Promise<Choices[]> => {
  const url = ApiEndpoints.createUrlWithPathVariables("bx", "choices", [
    choice,
  ]);
  const response = await bxApiClient.get(url);
  // Transform response.data.result from { key, value } to { label: key, value }
  const transformed = response.data.result;
  return transformed;
};

export const getPremisesForBillCycle = async (queryParams: {
  category?: string[];
  subCategory?: string[];
  area?: string;
  subArea?: string;
  zone?: string;
  division?: string;
  limit?: number;
  page?: number;
  search?: string;
  remoteUtilityId?: string;
  premises?: string[];
  excludePremises?: boolean;
}): Promise<PaginatedResponsePremise> => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    "get-premises-for-bill-cycle",
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", queryParams.remoteUtilityId);

      if (queryParams.limit) qs.push("limit", queryParams.limit);
      if (queryParams.page) qs.push("page", queryParams.page);

      // arrays
      if (Array.isArray(queryParams.category)) {
        queryParams.category.forEach((value) => {
          qs.push("category", value);
        });
      }

      if (Array.isArray(queryParams.subCategory)) {
        queryParams.subCategory.forEach((value) => {
          qs.push("sub_category", value);
        });
      }

      // optional single values
      if (queryParams.zone) qs.push("zone", queryParams.zone);
      if (queryParams.division) qs.push("division", queryParams.division);
      if (queryParams.area) qs.push("area", queryParams.area);
      if (queryParams.subArea) qs.push("sub_area", queryParams.subArea);
      if (queryParams.search) qs.push("search_data", queryParams.search);

      if (Array.isArray(queryParams.premises)) {
        queryParams.premises.forEach((value) => {
          if (queryParams.excludePremises) {
            qs.push("excluded_premises", value);
          } else {
            qs.push("premises", value);
          }
        });
      }
    }
  );

  const response = await bxApiClient.get(url);

  return response.data;
};

export const getTerritoryConfigMap = async (
  configLevel: "zone" | "division" | "area" | "sub_area" | "premise" = "zone",
  codeList?: string
): Promise<SubArea[]> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "onboarding",
    `config-map/territory_json`,
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("config_level", configLevel);
      if (codeList) {
        qs.push("code_list", codeList);
      }
    }
  );
  const response = await onboardingApiClient.get(url);

  const data = response.data?.result;

  return data.map((item: any) => ({
    id: item.code,
    name: item.name,
  }));
};

export const getBillCycleDetails = async (
  billCycleId: string | number
): Promise<BillCycleDetail> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithPathVariables(
    "bx",
    `billcycle/details`,
    [billCycleId]
  );

  const response = await bxApiClient.get(url);
  return response.data?.result;
};

export const updateBillCycle = async (payload: any, billCycleId) => {
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `billcycle/${billCycleId}`
  );
  const response = await bxApiClient.put(url, payload);
  return response.data;
};

export const archiveBillCycle = async (billCycleId) => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
    "bx",
    `archive-bill`,
    [billCycleId],
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
    }
  );
  const response = await bxApiClient.put(url);
  return response.data;
};

export const addPremiseToBillCycle = async (billCycleId, payload) => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrl(
    "bx",
    `bill-cycle/${billCycleId}/add-premise`,
  );
  const response = await bxApiClient.post(url, payload);
  return response.data;
};

export const getBillCycleLastRunDetails = async (
  billCycleId: string | number
): Promise<BillRunData> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    `latest-run-summary`,
    (qs: QueryStringParameters) => {
        qs.push("remote_utility_id", REMOTE_UTILITY_ID),
        qs.push("bill_cycle_id", billCycleId),
        qs.push("page", 1),
        qs.push("is_extra_detail", 1);
    }

  );

  const response = await bxApiClient.get(url);
  return response.data?.result;
};

export const getBillCycleListForMovePremise = async (
  queryParams: Record<string, any>
): Promise<PaginatedBillCycleResponse> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();
  const url = ApiEndpoints.createUrlWithQueryParameters(
    "bx",
    "billschedule",
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("is_bill_cycle", 1);
      Object.entries(queryParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          qs.push(key, String(value));
        }
      });
    }
  );

  const response = await bxApiClient.get(url);
  return response.data;
};

export const moveBillCycle = async (payload: any) => {
  const url = ApiEndpoints.createUrlWithQueryParameters("bx", `bill-cycle/move-premises`);
  const response = await bxApiClient.post(url, payload);
  return response.data;
};

const isValidParam = (value: any) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

export const getOutstandingBills = async (
  queryParams: Record<string, any>,
  billCycleId: string | number
): Promise<PaginatedOutstandingBillsResponse> => {
  const REMOTE_UTILITY_ID = getRemoteUtilityId();

  const url = ApiEndpoints.createUrlWithQueryParameters(
    "cx",
    "outstanding-bills",
    (qs: QueryStringParameters) => {
      qs.push("remote_utility_id", REMOTE_UTILITY_ID);
      qs.push("bill_cycle_id", billCycleId.toString());
      qs.push("limit", PAGE_SIZE);
      qs.push('is_extra_details', 1);
      Object.entries(queryParams).forEach(([key, value]) => {
        if (!isValidParam(value)) return;

        qs.push(toSnakeCase(key), value);
      });
    }
  );

  const response = await cxApiClient.get(url);
  return response.data;
};